import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, Star, Plus, Eye, Grid, List, BookOpen, Zap } from 'lucide-react';
import LearningComponentsService from '../services/learningComponentsService';
import AddComponentModal from '../components/admin/AddComponentModal';
import { COMPONENT_CATEGORIES, COMPONENT_TYPES } from '../data/learningComponentsLibrary';
import './InteractiveLibrary.css';

const InteractiveLibrary = () => {
  const [components, setComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showExistingOnly, setShowExistingOnly] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);

  const filterComponents = useCallback(() => {
    let filtered = [...components];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(comp =>
        (comp.name && comp.name.toLowerCase().includes(term)) ||
        (comp.description && comp.description.toLowerCase().includes(term)) ||
        ((comp.tags || []).some(tag => String(tag).toLowerCase().includes(term)))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(comp => comp.category === selectedCategory);
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(comp => comp.type === selectedType);
    }

    // Filter existing features only
    if (showExistingOnly) {
      filtered = filtered.filter(comp => comp.existingFeature);
    }

    setFilteredComponents(filtered);
  }, [components, searchTerm, selectedCategory, selectedType, showExistingOnly]);

  useEffect(() => {
    loadComponents();
  }, []);

  useEffect(() => {
    filterComponents();
  }, [filterComponents]);

  const [usingFallbackLibrary, setUsingFallbackLibrary] = useState(false);

  const loadComponents = async () => {
    try {
      setLoading(true);
      setUsingFallbackLibrary(false);
      const allComponents = await LearningComponentsService.getAllComponents();
      const list = Array.isArray(allComponents) ? allComponents : [];
      if (list.length === 0) {
        const { LEARNING_COMPONENTS_LIBRARY } = await import('../data/learningComponentsLibrary');
        setComponents(Array.isArray(LEARNING_COMPONENTS_LIBRARY) ? LEARNING_COMPONENTS_LIBRARY : []);
        setUsingFallbackLibrary(true);
      } else {
        setComponents(list);
      }
    } catch (error) {
      console.error('Error loading components:', error);
      const { LEARNING_COMPONENTS_LIBRARY } = await import('../data/learningComponentsLibrary');
      setComponents(Array.isArray(LEARNING_COMPONENTS_LIBRARY) ? LEARNING_COMPONENTS_LIBRARY : []);
      setUsingFallbackLibrary(true);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '#4CAF50';
      case 'intermediate': return '#FF9800';
      case 'advanced': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getTypeIcon = (type) => {
    const typeInfo = COMPONENT_TYPES.find(t => t.name.toLowerCase() === type);
    return typeInfo ? typeInfo.icon : '📦';
  };

  const getCategoryInfo = (category) => {
    return COMPONENT_CATEGORIES.find(c => c.name === category);
  };

  const handleAddToCourse = (component) => {
    setSelectedComponent(component);
    setShowAddModal(true);
  };

  const handleAddToSession = (component) => {
    setSelectedComponent(component);
    setShowAddModal(true);
  };

  const handleModalSuccess = () => {
    loadComponents();
  };

  const handlePreview = (component) => {
    if (component.route) {
      window.open(component.route, '_blank', 'noopener,noreferrer');
    } else {
      alert('Preview not available for this component.');
    }
  };

  const existingFeatures = components.filter(comp => comp.existingFeature);
  const newComponents = components.filter(comp => !comp.existingFeature);

  if (loading) {
    return (
      <div className="interactive-library-loading">
        <div className="loading-spinner"></div>
        <p>Loading Interactive Library...</p>
      </div>
    );
  }

  return (
    <div className="interactive-library">
      {/* Header */}
      <div className="library-header">
        <div className="header-content">
          <h1>
            <Zap size={32} />
            Interactive Library
          </h1>
          <p>Discover and manage interactive learning components for your courses</p>
        </div>
        
        <div className="header-stats">
          <div className="stat">
            <span className="stat-value">{components.length}</span>
            <span className="stat-label">Total Components</span>
          </div>
          <div className="stat">
            <span className="stat-value">{existingFeatures.length}</span>
            <span className="stat-label">Existing Features</span>
          </div>
          <div className="stat">
            <span className="stat-value">{newComponents.length}</span>
            <span className="stat-label">New Components</span>
          </div>
        </div>
      </div>

      {/* Quick Access - Existing Features */}
      <div className="quick-access-section">
        <h2>
          <Star size={20} />
          Existing Learn for Fun Features
        </h2>
        <div className="existing-features-grid">
          {existingFeatures.map(feature => (
            <Link 
              key={feature.id || feature.name} 
              to={feature.route || '#'}
              className="existing-feature-card"
            >
              <div className="feature-icon">
                {getTypeIcon(feature.type)}
              </div>
              <div className="feature-content">
                <h3>{feature.name}</h3>
                <p>{feature.description}</p>
                <div className="feature-meta">
                  <span className="feature-category">
                    {getCategoryInfo(feature.category)?.icon} {feature.category}
                  </span>
                  <span className="feature-difficulty" style={{ backgroundColor: getDifficultyColor(feature.difficulty) }}>
                    {feature.difficulty}
                  </span>
                </div>
              </div>
              <div className="feature-arrow">→</div>
            </Link>
          ))}
        </div>
      </div>

      {usingFallbackLibrary && (
        <div className="library-fallback-notice" style={{ background: '#fff3cd', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
          Showing catalog from static library (Firestore learning components unavailable or empty). “Add to Course/Session” works only for components that exist in the library—use Admin to add them first.
        </div>
      )}

      {/* Search and Filters */}
      <div className="library-filters">
        <div className="search-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search interactive components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-controls">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {COMPONENT_CATEGORIES.map(cat => (
                <option key={cat.name} value={cat.name}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Types</option>
              {COMPONENT_TYPES.map(type => (
                <option key={type.name} value={type.name.toLowerCase()}>
                  {type.icon} {type.name}
                </option>
              ))}
            </select>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showExistingOnly}
                onChange={(e) => setShowExistingOnly(e.target.checked)}
              />
              Existing features only
            </label>
          </div>
        </div>

        <div className="view-controls">
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <Grid size={18} />
          </button>
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Components Display */}
      <div className="components-section">
        <h2>
          <BookOpen size={20} />
          Available Components ({filteredComponents.length})
        </h2>

        {filteredComponents.length === 0 ? (
          <div className="no-components">
            <p>No components found matching your criteria.</p>
          </div>
        ) : (
          <div className={`components-container ${viewMode}`}>
            {filteredComponents.map(component => {
              const categoryInfo = getCategoryInfo(component.category);
              const isExisting = component.existingFeature;
              
              return (
                <div
                  key={component.id || component.name}
                  className={`component-card ${isExisting ? 'existing' : ''}`}
                >
                  <div className="card-header">
                    <div className="component-type">
                      {getTypeIcon(component.type)}
                    </div>
                    {isExisting && (
                      <div className="existing-badge">
                        <Star size={14} />
                        Existing
                      </div>
                    )}
                    <div className="component-actions">
                      <button
                        className="action-btn preview-btn"
                        title="Preview component"
                        onClick={() => handlePreview(component)}
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="card-content">
                    <h3>{component.name}</h3>
                    <p className="description">{component.description}</p>
                    
                    <div className="component-meta">
                      <div className="meta-item">
                        <div 
                          className="category-badge"
                          style={{ backgroundColor: categoryInfo?.color || '#9E9E9E' }}
                        >
                          {categoryInfo?.icon} {component.category}
                        </div>
                      </div>
                      
                      <div className="meta-item">
                        <div 
                          className="difficulty-badge"
                          style={{ backgroundColor: getDifficultyColor(component.difficulty) }}
                        >
                          {component.difficulty}
                        </div>
                      </div>
                      
                      <div className="meta-item">
                        <Clock size={14} />
                        <span>{component.estimatedTime}m</span>
                      </div>
                    </div>

                    {(component.tags || []).length > 0 && (
                      <div className="component-tags">
                        {(component.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                        {(component.tags || []).length > 3 && (
                          <span className="tag">+{(component.tags || []).length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="card-footer">
                    {!isExisting && (
                      <div className="add-buttons">
                        <button
                          className="add-btn course-btn"
                          onClick={() => handleAddToCourse(component)}
                        >
                          <Plus size={14} />
                          Add to Course
                        </button>
                        <button
                          className="add-btn session-btn"
                          onClick={() => handleAddToSession(component)}
                        >
                          <Plus size={14} />
                          Add to Session
                        </button>
                      </div>
                    )}
                    {isExisting && component.route && (
                      <Link to={component.route} className="access-btn">
                        Access Feature
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Component Modal */}
      {selectedComponent && (
        <AddComponentModal
          component={selectedComponent}
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default InteractiveLibrary;
